import React, { useEffect, useState } from "react";
import MainLayout from "../components/layout/MainLayout";

import { useTranslation } from "../core/translation.core";
import { useCart } from "../hooks/cart/useCart.hook";
import type { UpdateCartProduct } from "../types/cart";
import CheckoutProgress from "../components/checkout/checkoutProgress.comp";
import { CartItemComp } from "../components/cart/cartItem.comp";
import CartOrderSummary from "../components/cart/cartOrderSummary.comp";

import CombineOrder from "../components/cart/combineOrder.comp";
import type { GiftPromotion } from "../types/checkout";
import Image from "next/image";
import Link from "next/link";
import { getProductUrl } from "../core/productUrl.core";
import useCheckoutSession from "../components/common/useCheckoutSession.hook";
import { LoadingComp } from "../components/common/loading.comp";
import { formatCurrency } from "../core/currency.core";
import { AddOnProductsSection } from "../components/cart/addOnProductsSection.comp";
import GiftGroupSelector from "../components/cart/giftGroupSelector.comp";
import { InsufficientStockErrorItem, readInsufficientStockError } from "../core/insufficientStock.core";

export default function CartPage() {
  const { t, msg } = useTranslation();

  return (
    <MainLayout title={t(msg.cart.cart)} url="/cart">
      <CartPageContent />
    </MainLayout>
  );
}

function CartPageContent() {
  const { t, msg } = useTranslation();
  const [insufficientStockItems, setInsufficientStockItems] = useState<InsufficientStockErrorItem[]>([]);

  useEffect(() => {
    const items = readInsufficientStockError();
    if (items?.length) {
      setInsufficientStockItems(items);
    }
  }, []);

  return (
    <div className="min-h-screen bg-guru-floral-white">
      <div className="max-w-[1200px] mx-auto px-0 md:px-4 py-6">
        <h1 className="text-2xl mb-6 px-4 md:px-0">{t(msg.cart.cart)}</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-2/3">
            <div className="bg-white rounded-lg overflow-hidden">
              <CheckoutProgress currentStep="cart" className="mx-4 my-4" />
              {insufficientStockItems.length > 0 ? (
                <InsufficientStockBanner items={insufficientStockItems} />
              ) : null}
              <CartIsEmpty />
              <CartItems insufficientStockItems={insufficientStockItems} />
              <GiftPromotionItems asCard={false} />
            </div>

            <NonFreeShippingItems insufficientStockItems={insufficientStockItems} />
            <AddOnProductsSection />
            <CombineOrder />
          </div>

          <CartOrderSummary />
        </div>
      </div>
    </div>
  );
}

function InsufficientStockBanner({ items }: { items: InsufficientStockErrorItem[] }) {
  const { t, msg } = useTranslation();

  return (
    <div className="mx-4 mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
      <p className="text-sm font-medium text-red-700">{t(msg.cart.insufficientStockBanner)}</p>
      <ul className="mt-2 space-y-1">
        {items.map((item) => (
          <li key={item.productId} className="text-sm text-red-600">
            {t(msg.cart.insufficientStockItem)
              .replace("{name}", item.name)
              .replace("{requested}", String(item.requestedQuantity))
              .replace("{available}", String(item.availableQuantity))}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NonFreeShippingItems({ insufficientStockItems }: { insufficientStockItems: InsufficientStockErrorItem[] }) {
  const { t, msg } = useTranslation();
  const { cart, isLoading, updateItem, removeItem } = useCart();

  const handleRemoveItem = ({ cartItemId, productId }: { productId: string; cartItemId: string }) => {
    removeItem({ productId, cartItemId });
  };

  const handleUpdateItem = async (updateData: UpdateCartProduct) => {
    await updateItem(updateData);
  };

  const cartData = cart;
  const nonFreeShippingItems = cartData?.items.filter((item) => !item.product.allowFreeShipping);

  if (!nonFreeShippingItems) return null;
  if (nonFreeShippingItems.length === 0) return null;
  if (!cartData) return null;
  if (isLoading) return null;

  return (
    <div className="bg-white rounded-lg overflow-hidden mt-4">
      <div className="px-4 py-2 text-center bg-gray-100">{t(msg.cart.nonFreeShippingItems)}</div>
      {nonFreeShippingItems.map((item, index) => (
        <CartItemComp
          key={item.id}
          item={item}
          index={index}
          totalItems={cartData.items.length}
          onRemove={() => handleRemoveItem({ cartItemId: item.id, productId: item.product.id })}
          onUpdateQuantity={(quantity) => handleUpdateItem({ cartItemId: item.id, product: item.product, quantity })}
          allCartItems={cartData.items}
          insufficientStockItem={insufficientStockItems.find((stockItem) => stockItem.productId === item.product.id)}
        />
      ))}
    </div>
  );
}

function GiftPromotionItems({ asCard = true }: { asCard?: boolean }) {
  const { t, msg } = useTranslation();
  const { data: checkoutSession, isError, isLoading } = useCheckoutSession(true);

  if (!checkoutSession) return null;
  if (!checkoutSession.giftPromotions) return null;
  if (checkoutSession.giftPromotions.length === 0) return null;

  if (isError) return <div className="text-red-500">{t(msg.general.errSubmit)}</div>;
  if (isLoading) {
    return <LoadingComp />;
  }

  const giftItems = checkoutSession.giftPromotions.flatMap((promotion: GiftPromotion) =>
    promotion.giftItems.map((item) => ({
      product: item.product,
      quantity: item.quantity,
      promotionId: promotion.promotionId,
      isFallback: item.isFallback,
      promotionName: promotion.name,
      promotionDescription: promotion.description,
    }))
  );

  const content = (
    <>
      <div className="px-4 py-2 text-center bg-guru-liver text-white">{t(msg.cart.giftPromotionItems)}</div>
      {giftItems.map((item, index: number) => (
        <div
          key={`${item.promotionId}-${item.product.id}-${index}`}
          className={`py-6 px-6 ${index !== giftItems.length - 1 ? "border-b border-gray-100" : ""}`}
        >
          <div className="flex gap-6 items-center">
            {/* Image section */}
            <div className="relative w-24 h-24 flex-shrink-0">
              <Link href={getProductUrl(item.product)}>
                {item.product.featureMedia ? (
                  <Image
                    src={item.product.featureMedia.url}
                    width={96}
                    height={96}
                    alt={item.product.name}
                    className="object-contain"
                    unoptimized
                  />
                ) : (
                  <Image
                    src="/images/logo-square.webp"
                    width={96}
                    height={96}
                    alt={item.product.name}
                    className="object-contain"
                  />
                )}
              </Link>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  <h3 className="text-base font-medium leading-6 line-clamp-2">
                    <Link href={getProductUrl(item.product)}>{item.product.name}</Link>
                  </h3>
                  <div className="text-sm font-normal mt-2">
                    <p className="text-gray-600">原價: {formatCurrency(item.product.price, item.product.currency)}</p>
                    <p className="text-gray-600">
                      {t(msg.cart.quantity)}: {item.quantity}
                    </p>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm text-green-600 font-medium">{t(msg.cart.free)}</span>
                  </div>

                  <div className="mt-2 text-sm text-gray-600 font-medium">{item.promotionName}</div>
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">{item.promotionDescription}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      {checkoutSession.giftPromotions.map((promotion: GiftPromotion) =>
        (promotion.giftItemGroups ?? []).map((group, groupIndex: number) => (
          <div key={`group-${promotion.promotionId}-${groupIndex}`} className="py-4 px-6 border-t border-gray-100">
            <GiftGroupSelector promotion={promotion} group={group} groupIndex={groupIndex} isTemp />
          </div>
        ))
      )}
    </>
  );

  if (!asCard) {
    return <div className="border-t border-gray-100">{content}</div>;
  }

  return <div className="bg-white rounded-lg overflow-hidden mt-4">{content}</div>;
}

function CartItems({ insufficientStockItems }: { insufficientStockItems: InsufficientStockErrorItem[] }) {
  const { cart: cartData, isLoading, removeItem, updateItem } = useCart();

  function handleRemoveItem({ cartItemId, productId }: { productId: string; cartItemId: string }) {
    removeItem({ productId, cartItemId });
  }

  async function handleUpdateItem(updateData: UpdateCartProduct) {
    await updateItem(updateData);
  }

  if (!cartData) return null;
  if (isLoading) return null;

  return (
    <div>
      {cartData.items
        .filter((item) => item.product.allowFreeShipping)
        .map((item, index) => (
          <CartItemComp
            key={item.id}
            item={item}
            index={index}
            totalItems={cartData.items.length}
            onRemove={() => handleRemoveItem({ cartItemId: item.id, productId: item.product.id })}
            onUpdateQuantity={(quantity) => handleUpdateItem({ cartItemId: item.id, product: item.product, quantity })}
            allCartItems={cartData.items}
            insufficientStockItem={insufficientStockItems.find((stockItem) => stockItem.productId === item.product.id)}
          />
        ))}
    </div>
  );
}

function CartIsEmpty() {
  const { t, msg } = useTranslation();
  const { cart: cartData, isLoading } = useCart();

  if (!cartData) return null;

  if (isLoading) {
    return (
      <div className="h-96 bg-guru-floral-white flex items-center justify-center">
        <LoadingComp />
      </div>
    );
  }

  if (cartData.items.length !== 0) return null;

  return (
    <div className="min-h-screen bg-guru-floral-white">
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <h1 className="text-2xl mb-6">{t(msg.cart.cart)}</h1>
        <div className="bg-white rounded-lg p-8 text-center">
          <p className="text-gray-600">{t(msg.product.cartIsEmpty)}</p>
        </div>
      </div>
    </div>
  );
}
